import { Injectable, WritableSignal } from '@angular/core';
import { LegTourPointConfig, MarkLegStaysLoadedRequest, MarkLegStaysLoadedResponse } from '../../models/planningPageTypes';
import { protectSubscription } from '@nagel-utils';
import { environment } from 'apps/nagel-cal-disposition/environment/environment';
import { RequestService, SnackbarService } from '@nagel-services';
import { tmsLegsResourceEndpoint } from '../configuration/consts/endpoints';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '@nagel-types';
import { DriveInstructionsDrawerService } from './drive-instructions-drawer.service';

@Injectable({
    providedIn: 'root'
})
export class AssignedLegManagementService {

    constructor(
      private requestService: RequestService,
      private snackbarService: SnackbarService,
      private driveInstructionsDrawerService: DriveInstructionsDrawerService,
    ) { }

    markLegStaysLoaded(
        legConfig: LegTourPointConfig,
        staysLoaded: WritableSignal<boolean>
    ): void {
        const shouldStayLoaded = !staysLoaded();

        const requestObservable = this.executeMarkLegStaysLoaded(legConfig.legId, shouldStayLoaded);

        protectSubscription<MarkLegStaysLoadedResponse>(
            requestObservable,
            this.onMarkLegStaysLoadedSuccess(legConfig, shouldStayLoaded, staysLoaded),
            this.onMarkLegStaysLoadedError(),
            true
        );
    }

    private onMarkLegStaysLoadedSuccess = (
        legConfig: LegTourPointConfig,
        shouldStayLoaded: boolean,
        staysLoaded: WritableSignal<boolean>
    ) => (response: MarkLegStaysLoadedResponse): void => {
        const loadedString = shouldStayLoaded ? $localize`should stay loaded` : $localize`should be unloaded`;
        const successfulMessage = $localize`Leg successfully marked as ${loadedString}.`;

        if (response.isStaysLoadedSet) {
            legConfig.staysLoaded = shouldStayLoaded;
            staysLoaded.set(shouldStayLoaded);

            this.snackbarService.pushSnackbar({
                message: successfulMessage,
                action: 'OK',
                type: SUCCESS_SNACKBAR
            });

            const { transportOrderId } = this.driveInstructionsDrawerService.transportOrderDataSubject.value;
            this.driveInstructionsDrawerService.getTourPointsData(transportOrderId, false);
        } else {
            this.showErrorSnackbar($localize`Update of stays loaded failed.`);
        };
    };

    private onMarkLegStaysLoadedError = () => (error: unknown): void => {
        console.error('Error marking leg stays loaded:', error);
    };

    private showErrorSnackbar(message: string): void {
        this.snackbarService.pushSnackbar({
            message,
            action: 'OK',
            type: ERROR_SNACKBAR
        });
    }

    private executeMarkLegStaysLoaded(legId: string, staysLoadedValue: boolean) {
        const url = `${environment.apiUrl}${tmsLegsResourceEndpoint}/${legId}/stays-loaded?staysLoadedValue=${staysLoadedValue}`;
        return this.requestService.patchRequest<MarkLegStaysLoadedRequest, MarkLegStaysLoadedResponse>(url);
    }
}
